import React, {
  ComponentPropsWithRef,
  ElementType,
  forwardRef,
  MouseEvent,
  ReactNode,
  Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { ChevronFirst } from 'lucide-react';
import { cn } from '@/utils/utils.ts';
import { useSidebarStore } from '@/services/store/use-sidebar-store.tsx';
import { ChevronDownIcon } from '@radix-ui/react-icons';

export interface CollapseProps extends ComponentPropsWithRef<'div'> {
  isOpen: boolean;
  duration?: number;
}

const Collapse = (props: CollapseProps) => {
  const collapseRef = useRef<HTMLDivElement>(null);
  const { duration = 250, isOpen = false, children } = props;
  const mounted = useRef(true);

  useEffect(() => {
    const element = collapseRef.current;

    if (!element) {
      return;
    }

    if (!element.animate) {
      element.style.height = isOpen ? 'auto' : '0';
      return;
    }

    if (mounted.current) {
      element.style.height = isOpen ? 'auto' : '0';
      mounted.current = false;
      return;
    }

    const activeAnimations = element.getAnimations();
    const currentHeight = getComputedStyle(element).height;
    const endHeight = isOpen ? `${element.scrollHeight}px` : '0';

    for (const activeAnimation of activeAnimations) {
      activeAnimation.cancel();
    }

    const heightAnimation = [currentHeight, endHeight];
    const keyframes = {
      height: heightAnimation,
    };

    const animation: Animation = element.animate(keyframes, {
      duration,
      easing: 'ease-in-out',
    });

    animation.onfinish = () => {
      element.style.overflow = endHeight;
      element.style.height = isOpen ? 'auto' : '0px';
    };
  }, [duration, isOpen]);

  return (
    <div
      ref={collapseRef}
      style={{
        height: '0px',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
};

Collapse.displayName = 'Collapse';

export default Collapse;

const base = cva(
  'antialiased transition-width ease-in-out duration-300 flex flex-col fixed top-0 left-0 h-full z-50',
  {
    variants: {
      color: {
        white: 'bg-white text-gray-700 border-r',
        dark: 'bg-gray-900 text-gray-200 shadow-gray-950',
      },
      shadow: {
        none: 'shadow-none',
        sm: 'shadow-xs',
        base: 'shadow-sm',
        md: 'shadow-md',
        lg: 'shadow-lg',
        xl: 'shadow-xl',
      },
      expanded: {
        true: 'sm:min-w-[17rem]',
        false: '',
      },
      hovered: {
        true: 'sm:min-w-[17rem]',
        false: '',
      },
      mobile: {
        true: 'w-[16rem] overflow-y-auto sm:overflow-y-visible',
        false: 'w-0',
      },
    },
    compoundVariants: [
      {
        expanded: false,
        hovered: false,
        className: 'sm:min-w-[5rem]',
      },
    ],
  }
);

export const SidebarRoot = forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, children }: React.HTMLAttributes<HTMLElement>, ref?: Ref<HTMLElement>) => {
    const id = useId();
    const isExpanded = useSidebarStore((s) => s.isExpanded);
    const isHovered = useSidebarStore((s) => s.isHovered);
    const setIsHovered = useSidebarStore((s) => s.setIsHovered);

    return (
      <aside
        id={id}
        ref={ref}
        className={cn(
          base({
            color: 'white',
            shadow: 'sm',
            expanded: isExpanded,
            hovered: isHovered,
            mobile: false,
            className,
          })
        )}
        onMouseEnter={(event: MouseEvent<HTMLElement>) => {
          const target = event.target as HTMLElement;

          if (target.dataset.role === 'sidebar-head-toggle') {
            return;
          }

          if (isExpanded) {
            return;
          }

          setIsHovered(true);
        }}
        onMouseLeave={() => {
          if (isExpanded) {
            return;
          }

          setIsHovered(false);
        }}
      >
        {children}
      </aside>
    );
  }
);

SidebarRoot.displayName = 'Sidebar';

//
// Head Root
//

const headRootVariants = cva(
  'transition-opacity top-0 w-full items-center min-h-[5rem] px-4 border-b border-dashed bg-inherit flex flex-nowrap truncate',
  {
    variants: {
      mobile: {
        true: 'opacity-100',
        false: 'opacity-0 sm:opacity-100',
      },
      color: {
        white: 'border-gray-200',
        dark: 'border-gray-700',
      },
    },
  }
);

export const SidebarHeadRoot = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(
  (
    { children, className, ...additionalProps }: React.HTMLAttributes<HTMLDivElement>,
    ref?: Ref<HTMLDivElement>
  ) => {
    const id = useId();

    return (
      <div
        id={id}
        className={cn(headRootVariants({ color: 'white', mobile: false, className }))}
        ref={ref}
        {...additionalProps}
      >
        {children}
      </div>
    );
  }
);

SidebarHeadRoot.displayName = 'SidebarHead';

//
// Head Logo
//

const headLogoVariants = cva('h-10 flex flex-none items-center justify-center');

export interface ISidebarHeadLogoProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof headLogoVariants> {}

export const SidebarHeadLogo = forwardRef<HTMLDivElement, ISidebarHeadLogoProps>(
  ({ children, className }: ISidebarHeadLogoProps, ref?: Ref<HTMLDivElement>) => {
    const id = useId();

    return (
      <div id={id} ref={ref} className={cn(headLogoVariants({ className }))}>
        {children}
      </div>
    );
  }
);

SidebarHeadLogo.displayName = 'SidebarHeadLogo';

//
// Head Title
//

const headTitleVariants = cva('text-lg font-medium truncate', {
  variants: {
    opened: {
      true: 'ml-2',
      false: '',
    },
  },
});

export interface ISidebarHeadTitleProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof headTitleVariants> {}

export const SidebarHeadTitle = forwardRef<HTMLDivElement, ISidebarHeadTitleProps>(
  ({ children, className }: ISidebarHeadTitleProps, ref?: Ref<HTMLDivElement>) => {
    const id = useId();
    const isExpanded = useSidebarStore((s) => s.isExpanded);
    const isHovered = useSidebarStore((s) => s.isHovered);

    return (
      <div
        id={id}
        ref={ref}
        className={cn(
          headTitleVariants({
            opened: isExpanded || isHovered,
            className,
          })
        )}
      >
        {(isExpanded || isHovered) && children}
      </div>
    );
  }
);

SidebarHeadTitle.displayName = 'SidebarHeadTitle';

//
// Head Toggle
//

const headToggleVariants = cva(
  'hidden sm:flex items-center w-7 h-7 p-1 rounded shadow-sm ring-1 absolute right-0 translate-x-1/2',
  {
    variants: {
      color: {
        white: 'ring-gray-100 bg-white text-gray-500',
        dark: 'ring-gray-100 bg-white text-gray-500',
      },
    },
  }
);

const headToggleIconVariants = cva('transition, transform, duration-250, ease-in-out', {
  variants: {
    expanded: {
      true: '',
      false: 'rotate-180',
    },
  },
});

export interface ISidebarHeadToggleProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof headToggleIconVariants> {}

export const SidebarHeadToggle = forwardRef<HTMLButtonElement, ISidebarHeadToggleProps>(
  (
    { className, onClick, ...additionalProps }: ISidebarHeadToggleProps,
    ref?: Ref<HTMLButtonElement>
  ) => {
    const id = useId();
    const isExpanded = useSidebarStore((s) => s.isExpanded);
    const toggleIsExpanded = useSidebarStore((s) => s.toggleIsExpanded);

    return (
      <button
        id={id}
        ref={ref}
        type="button"
        title={isExpanded ? 'Collapse' : 'Expand'}
        aria-pressed={isExpanded}
        aria-expanded={isExpanded}
        data-role="sidebar-head-toggle"
        className={cn(headToggleVariants({ color: 'white', className }))}
        onClick={(event) => {
          toggleIsExpanded();

          if (onClick) {
            onClick(event);
          }
        }}
        {...additionalProps}
      >
        {/* <LeftToLineIcon
          className={cn(headToggleIconVariants({ expanded: isExpanded }))}
        /> */}
        <ChevronFirst
          className={cn(headToggleIconVariants({ expanded: isExpanded }), 'size-10')}
        />
      </button>
    );
  }
);

SidebarHeadToggle.displayName = 'SidebarHeadToggle';

//
// Nav Root
//

const navigationVariants = cva(
  'my-3 overflow-x-hidden scrollbar-thin scrollbar-thumb-rounded-lg scrollbar-track-rounded-lg flex flex-col gap-y-6',
  {
    variants: {
      color: {
        white: 'scrollbar-thumb-gray-200',
        dark: 'scrollbar-thumb-gray-700',
      },
      opened: {
        true: 'overflow-y-auto',
        false: 'overflow-y-hidden',
      },
    },
  }
);

export const SidebarNavRoot = forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  (
    { children, className, ...additionalProps }: React.HTMLAttributes<HTMLElement>,
    ref?: Ref<HTMLElement>
  ) => {
    const id = useId();
    const isExpanded = useSidebarStore((s) => s.isExpanded);
    const isHovered = useSidebarStore((s) => s.isHovered);

    return (
      <nav
        id={id}
        className={cn(
          navigationVariants({
            color: 'white',
            opened: isExpanded || isHovered,
            className,
          })
        )}
        ref={ref}
        {...additionalProps}
      >
        {children}
      </nav>
    );
  }
);

SidebarNavRoot.displayName = 'SidebarNav';

//
// Nav Section
//

const navSectionVariants = cva('flex flex-col gap-1', {
  variants: {
    asChild: {
      true: '',
      false: '',
    },
    color: {
      white: '',
      dark: '',
    },
  },
  compoundVariants: [
    {
      asChild: true,
      color: 'white',
      className: 'bg-gray-100',
    },
    {
      asChild: true,
      color: 'dark',
      className: 'bg-gray-950',
    },
  ],
});

export interface ISidebarNavSectionProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
}

export const SidebarNavRootSection = forwardRef<HTMLUListElement, ISidebarNavSectionProps>(
  (
    { children, className, asChild = false, ...additionalProps }: ISidebarNavSectionProps,
    ref?: Ref<HTMLUListElement>
  ) => {
    const id = useId();

    return (
      <ul
        id={id}
        className={cn(navSectionVariants({ asChild, color: 'white', className }))}
        ref={ref}
        {...additionalProps}
      >
        {children}
      </ul>
    );
  }
);

SidebarNavRootSection.displayName = 'SidebarNavSection';

//
// Nav Section Title
//

const navSectionTitleVariants = cva(
  'transition-opacity text-sm font-medium truncate mb-1 px-5 mr-auto',
  {
    variants: {
      color: {
        white: 'text-gray-400',
        dark: 'text-gray-500',
      },
      opened: {
        true: 'opacity-100',
        false: 'opacity-0',
      },
    },
  }
);

export const SidebarNavSectionTitle = forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLElement>
>(
  (
    { children, className, ...additionalProps }: React.HTMLAttributes<HTMLElement>,
    ref?: Ref<HTMLLIElement>
  ) => {
    const id = useId();
    const isExpanded = useSidebarStore((s) => s.isExpanded);
    const isHovered = useSidebarStore((s) => s.isHovered);

    return (
      <li
        id={id}
        ref={ref}
        className={cn(
          navSectionTitleVariants({
            color: 'white',
            opened: isExpanded || isHovered,
            className,
          })
        )}
        {...additionalProps}
      >
        {children}
      </li>
    );
  }
);

SidebarNavSectionTitle.displayName = 'SidebarNavSectionTitle';

//
// Nav Section Item
//

const navSectionItemWrapperVariants = cva(['cursor-pointer'], {
  variants: {
    opened: {
      true: [],
      false: ['px-[0.8rem]'],
    },
  },
});

const navSectionItemVariants = cva(
  [
    'group',
    'truncate',
    'w-full',
    'flex',
    'items-center',
    'text-sm',
    'outline-hidden',
    'ring-inset',
    'focus-visible:ring-2',
    'focus-visible:rounded',
    'py-2.5',
    'gap-x-3',
    'border',
    'font-medium',
  ],
  {
    variants: {
      color: {
        white: ['focus-visible:ring-gray-300 hover:bg-slate-50'],
        dark: ['focus-visible:ring-gray-400'],
      },
      active: {
        true: [],
        false: ['border-transparent'],
      },
      expanded: {
        true: [],
        false: [],
      },
      opened: {
        true: ['px-[1.65rem]', 'border-transparent'],
        false: ['justify-start', 'pl-[0.85rem] pr-[0.8rem]', 'rounded'],
      },
    },
    compoundVariants: [
      {
        active: true,
        color: 'white',
        className: [
          'bg-linear-to-tr from-slate-200 to-slate-100 text-sla te-800',
          'text-black',
        ],
      },
      {
        active: true,
        opened: false,
        color: 'white',
        className: ['border-gray-100'],
      },
      {
        active: false,
        color: 'white',
        className: ['text-gray-500', 'hover:text-gray-700'],
      },
      {
        active: true,
        color: 'dark',
        className: ['bg-gray-800', 'text-gray-100'],
      },
      {
        active: true,
        opened: false,
        color: 'dark',
        className: ['border-gray-700'],
      },
      {
        active: false,
        color: 'dark',
        className: ['text-gray-400', 'hover:text-gray-200'],
      },
    ],
  }
);

const navSectionItemIconVariants = cva([
  'w-6',
  'h-6',
  'flex-none',
  'flex',
  'items-center',
  'justify-center',
]);

const navSectionItemLabelWrapperVariants = cva(
  [
    'transition-opacity',
    'duration-250',
    'flex',
    'items-center',
    'justify-between',
    'w-full',
    'gap-2',
  ],
  {
    variants: {
      opened: {
        true: ['opacity-100'],
        false: ['opacity-0'],
      },
    },
  }
);

const navSectionItemLabelVariants = cva([
  'text-left',
  'text-base',
  'max-w-[140px]',
  'overflow-hidden',
  'text-ellipsis',
]);

const navSectionItemCollapseIconVariants = cva(
  ['transition', 'transform', 'duration-250', 'ease-in-out', 'h-4', 'w-4', 'ml-auto'],
  {
    variants: {
      collapsed: {
        true: [],
        false: ['rotate-180'],
      },
    },
  }
);

export interface SidebarNavSectionItemProps extends ComponentPropsWithRef<'li'> {
  as?: ElementType;
  asProps?: Record<string, string | number | boolean>;
  href?: string;
  icon?: ReactNode;
  statusChildren?: ReactNode;
  label: ReactNode;
  active?: boolean;
  collapsed?: boolean;
}

export const SidebarNavSectionItem = forwardRef<HTMLLIElement, SidebarNavSectionItemProps>(
  (
    {
      active = false,
      children,
      className,
      as = 'a',
      asProps,
      href = '#',
      label,
      icon,
      statusChildren,
      collapsed = true,
      onClick,
      ...additionalProps
    },
    ref?
  ) => {
    const id = useId();
    const isExpanded = useSidebarStore((s) => s.isExpanded);
    const isHovered = useSidebarStore((s) => s.isHovered);
    const [isSubSection, setIsSubSection] = useState<boolean>(false);
    const opened = isExpanded || isHovered;
    const [isCollapsed, setIsCollapsed] = useState<boolean>(!opened ? true : collapsed);

    const Component = as || 'a';
    const componentProps =
      as === 'button'
        ? ({ type: 'button' } as { type: 'button' | 'submit' | 'reset' })
        : { href };

    const liRef = useRef<HTMLLIElement | null>(null);

    useEffect(() => {
      // Function to check if the li has two ul parents
      const hasDoubleUlParent = (element: HTMLElement | null): boolean => {
        if (!element) return false;

        let ulParentCount = 0;
        let currentElement: HTMLElement | null = element.parentElement;

        while (currentElement && ulParentCount < 2) {
          if (currentElement.tagName.toLowerCase() === 'ul') {
            ulParentCount++;
          }
          currentElement = currentElement.parentElement;
        }

        return ulParentCount === 2;
      };

      // Check if the li has two ul parents when the component mounts
      if (liRef.current) {
        const hasTwoUlParents = hasDoubleUlParent(liRef.current);
        if (hasTwoUlParents) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setIsSubSection(true);
        }
      }
    }, []);

    return (
      <li
        id={id}
        ref={(node) => {
          // Merge the forwarded ref and our local ref
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          liRef.current = node;
        }}
        className={cn(
          navSectionItemWrapperVariants({
            opened,
          }),
          isSubSection && 'px-0'
        )}
        {...additionalProps}
      >
        <Component
          {...componentProps}
          {...asProps}
          className={cn(
            navSectionItemVariants({
              active,
              color: 'white',
              opened,
              className,
            })
          )}
          onClick={(event: React.MouseEvent<HTMLLIElement, globalThis.MouseEvent>) => {
            setIsCollapsed(!isCollapsed);

            if (onClick) {
              onClick(event);
            }
          }}
          tabIndex={isCollapsed ? -1 : 0}
        >
          {icon && <span className={cn(navSectionItemIconVariants())}>{icon}</span>}
          <div
            className={cn(
              navSectionItemLabelWrapperVariants({
                opened,
              })
            )}
          >
            <span className={cn(navSectionItemLabelVariants())}>{label}</span>

            {statusChildren}

            {children && (
              <ChevronDownIcon
                className={cn(
                  navSectionItemCollapseIconVariants({
                    collapsed: isCollapsed,
                  })
                )}
              />
            )}
          </div>
        </Component>
        <Collapse isOpen={!isCollapsed}>{children}</Collapse>
      </li>
    );
  }
);

SidebarNavSectionItem.displayName = 'SidebarNavSectionItem';

//
// Nav Separator
//

const separatorVariants = cva(['border-t', 'border-slate-700', 'border-dashed', 'my-2'], {
  variants: {
    color: {
      white: ['border-gray-200'],
      dark: ['border-gray-700'],
    },
  },
});

export const SidebarSeparator = forwardRef<HTMLHRElement, ComponentPropsWithRef<'hr'>>(
  ({ className, ...additionalProps }, ref?) => {
    const id = useId();

    return (
      <hr
        id={id}
        ref={ref}
        className={cn(separatorVariants({ color: 'white', className }))}
        {...additionalProps}
      />
    );
  }
);

SidebarSeparator.displayName = 'SidebarSeparator';

//
// Footer
//

const footerVariants = cva(
  'truncate w-full  justify-center min-h-[4rem] mt-auto sticky bottom-0 border-t border-dashed bg-inherit flex flex-col',
  {
    variants: {
      color: {
        white: 'border-gray-200',
        dark: 'border-gray-700',
      },
      mobile: {
        true: 'flex',
        false: 'hidden',
      },
      expanded: {
        true: 'sm:flex',
        false: 'sm:flex',
      },
      hovered: {
        true: 'sm:flex',
        false: [],
      },
    },
  }
);

export const SidebarFooter = forwardRef<HTMLHRElement, ComponentPropsWithRef<'div'>>(
  ({ children, className, ...additionalProps }, ref?) => {
    const id = useId();
    const isExpanded = useSidebarStore((s) => s.isExpanded);
    const isHovered = useSidebarStore((s) => s.isHovered);

    return (
      <div
        id={id}
        ref={ref}
        className={cn(
          footerVariants({
            color: 'white',
            expanded: isExpanded,
            hovered: isHovered,
            mobile: false,
            className,
          })
        )}
        {...additionalProps}
      >
        {children}
      </div>
    );
  }
);

SidebarFooter.displayName = 'SidebarFooter';
